import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { PostSchema } from "./postSchema";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import axios from "axios";

export const getIngredientPostKey = "getIngredientPost" as const;

type Options = {
    ingredientId: number | undefined;
    companyId: number | null | undefined;
    supplierId: number | null | undefined;
    campaignId?: number;
    scoresQuery?: OlapScoresQuery;
    enabled?: boolean;
};

export const useGetIngredientPost = (options: Options) => {
    const { ingredientId, companyId, supplierId, campaignId, scoresQuery, enabled = true } = options;

    const isEnabled =
        enabled &&
        ingredientId !== undefined &&
        companyId !== undefined &&
        companyId !== null &&
        supplierId !== undefined &&
        campaignId !== undefined;
    const query = useQuery({
        queryKey: [getIngredientPostKey, ingredientId, companyId, supplierId, campaignId, scoresQuery],
        cacheTime: 0,
        enabled: isEnabled,
        queryFn: () => fetchIngredientPost(ingredientId, companyId, supplierId, campaignId, scoresQuery),
        select: (response) => {
            if (!response) {
                return;
            }

            const post = response.data;
            const data = zodParseWithDetailedLog(post, PostSchema, {
                operation: "GET",
                endpoint: "/ingredients/:id/ssq-post",
            });
            return data;
        },
    });

    // Enforcing this because we want to get rid of the stale data;
    const post = isEnabled ? query.data : undefined;

    return {
        post,
        ...query,
    };
};

const fetchIngredientPost = async (
    ingredientId: number | undefined,
    companyId: number | null | undefined,
    supplierId: number | null | undefined,
    campaignId: number | undefined,
    scoresQuery?: OlapScoresQuery,
) => {
    const url = `/api/ingredients/${ingredientId}/ssq-post`;

    try {
        return await loggedInApiCall.get(url, {
            headers: {
                Accept: "application/json",
            },
            params: {
                organization_id: companyId ?? undefined,
                supplier_id: supplierId ?? undefined,
                campaign: campaignId ?? undefined,
                ...scoresQuery,
            },
        });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        throw error;
    }
};
